import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, Linkedin, ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';
import { format, isAfter, startOfDay } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
  '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM'
];

export default function AdvisorsBookPage() {
  const navigate = useNavigate();
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startupInfo, setStartupInfo] = useState(null);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [topic, setTopic] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('startup_token');
    const info = sessionStorage.getItem('startup_info');
    
    if (!token || !info) {
      navigate('/advisors/login');
      return;
    }
    
    setStartupInfo(JSON.parse(info));
    const fetchAdvisors = async () => {
      try {
        const response = await axios.get(`${API}/advisors`);
        setAdvisors(response.data);
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    };
    fetchAdvisors();
  }, [navigate]);

  const fetchAdvisorBookings = async (advisorId) => {
    try {
      const response = await axios.get(`${API}/bookings/advisor/${advisorId}`);
      setBookedSlots(response.data);
    } catch { /* silent */ }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('startup_token');
    sessionStorage.removeItem('startup_info');
    toast.success('Logged out successfully');
    navigate('/advisors');
  };

  const handleBookSession = async (advisor) => {
    setSelectedAdvisor(advisor);
    setBookingStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setTopic('');
    await fetchAdvisorBookings(advisor.id);
    setIsBookingModalOpen(true);
  };

  const isSlotBooked = (date, time) => {
    if (!date) return false;
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookedSlots.some(
      booking => booking.date === dateStr && booking.time_slot === time
    );
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (!selectedAdvisor || !selectedDate || !selectedTime || !startupInfo) return;

    setSubmitting(true);
    try {
      const token = sessionStorage.getItem('startup_token');
      await axios.post(`${API}/bookings`, {
        advisor_id: selectedAdvisor.id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedTime,
        name: startupInfo.founder_name,
        email: '', // Will be filled from startup info on backend
        startup_name: startupInfo.startup_name,
        topic: topic
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Booking confirmed!', {
        description: `Your session with ${selectedAdvisor.name} is scheduled for ${format(selectedDate, 'MMM d, yyyy')} at ${selectedTime}`
      });
      setIsBookingModalOpen(false);
    } catch (error) {
      toast.error('Booking failed', {
        description: error.response?.data?.detail || 'Please try again'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const disabledDays = (date) => {
    const today = startOfDay(new Date());
    return !isAfter(date, today) || date.getDay() === 0 || date.getDay() === 6;
  };

  if (!startupInfo) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#075FA3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#58595B]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Header Banner */}
      <section className="py-6 bg-[#075FA3]" data-testid="startup-header">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-white">
              <p className="text-sm text-white/70">Logged in as</p>
              <p className="text-xl font-bold font-['Bricolage_Grotesque']">{startupInfo.startup_name}</p>
              <p className="text-sm text-white/80">{startupInfo.founder_name}</p>
            </div>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#075FA3]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </section>

      {/* Advisors Grid */}
      <section className="section-padding bg-white" data-testid="advisors-book-grid">
        <div className="container-main">
          <div className="mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-[#231F20] mb-4 font-['Bricolage_Grotesque']">
              Book an Advisor Session
            </h1>
            <p className="text-[#58595B]">
              Select an advisor and schedule your one-on-one mentorship session
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-2xl h-80"></div>
                </div>
              ))}
            </div>
          ) : advisors.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {advisors.map((advisor, index) => (
                <motion.div
                  key={advisor.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    className="h-full border border-gray-200 hover:border-[#075FA3]/50 hover:shadow-xl transition-all duration-300 overflow-hidden group"
                    data-testid={`advisor-card-${advisor.id}`}
                  >
                    <CardContent className="p-0">
                      {/* Photo */}
                      <div className="h-56 bg-gradient-to-br from-[#075FA3]/10 to-[#4DBDDE]/10 overflow-hidden">
                        {advisor.photo_url ? (
                          <img 
                            src={advisor.photo_url} 
                            alt={advisor.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="w-24 h-24 rounded-full bg-[#075FA3] flex items-center justify-center text-white text-4xl font-bold font-['Bricolage_Grotesque']">
                              {advisor.name.charAt(0)}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-[#231F20] font-['Bricolage_Grotesque']">
                            {advisor.name}
                          </h3>
                          {advisor.linkedin_url && (
                            <a 
                              href={advisor.linkedin_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#075FA3] hover:text-[#0077B6]"
                            >
                              <Linkedin className="w-5 h-5" />
                            </a>
                          )}
                        </div>
                        <p className="text-[#C83041] text-sm font-medium mb-2">
                          {advisor.expertise}
                        </p>
                        <p className="text-sm text-[#58595B] mb-1">
                          {advisor.title}
                        </p>
                        <p className="text-sm text-[#58595B] line-clamp-2 mb-6">
                          {advisor.bio}
                        </p>
                        <Button 
                          onClick={() => handleBookSession(advisor)}
                          className="w-full bg-[#075FA3] hover:bg-[#0077B6] text-white rounded-full"
                          data-testid={`book-advisor-btn-${advisor.id}`}
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          Book a Session
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#F3F4F6] rounded-2xl p-12 text-center"
            >
              <div className="w-20 h-20 rounded-full bg-[#075FA3]/10 flex items-center justify-center mx-auto mb-6">
                <User className="w-10 h-10 text-[#075FA3]" />
              </div>
              <h3 className="text-xl font-semibold text-[#231F20] mb-3 font-['Bricolage_Grotesque']">
                Advisors Coming Soon
              </h3>
              <p className="text-[#58595B] max-w-md mx-auto">
                We&apos;re onboarding experienced advisors from our network. Check back soon!
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Booking Modal */}
      <Dialog open={isBookingModalOpen} onOpenChange={setIsBookingModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">
              Book a Session
            </DialogTitle>
            <DialogDescription className="text-[#58595B]">
              {selectedAdvisor?.name} • {selectedAdvisor?.expertise}
            </DialogDescription>
          </DialogHeader>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 my-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  bookingStep >= step 
                    ? 'bg-[#075FA3] text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-2 transition-colors ${
                    bookingStep > step ? 'bg-[#075FA3]' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-[#58595B] mb-6">
            <span>Select Date</span>
            <span>Select Time</span>
            <span>Confirm</span>
          </div>

          {/* Step 1: Select Date */}
          {bookingStep === 1 && (
            <div className="space-y-4">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={disabledDays}
                className="rounded-xl border border-gray-200 p-3"
                data-testid="booking-calendar"
              />
              <Button
                onClick={() => setBookingStep(2)}
                disabled={!selectedDate}
                className="w-full bg-[#075FA3] hover:bg-[#0077B6] text-white rounded-full py-6"
                data-testid="next-step-btn"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Select Time */}
          {bookingStep === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-[#58595B]">
                Selected date: <span className="font-semibold text-[#231F20]">
                  {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </span>
              </p>
              <div className="grid grid-cols-4 gap-2">
                {timeSlots.map((time) => {
                  const isBooked = isSlotBooked(selectedDate, time);
                  return (
                    <Button
                      key={time}
                      variant={selectedTime === time ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => !isBooked && setSelectedTime(time)}
                      disabled={isBooked}
                      className={`text-xs py-3 ${
                        selectedTime === time 
                          ? 'bg-[#075FA3] text-white' 
                          : isBooked 
                            ? 'opacity-50 cursor-not-allowed line-through' 
                            : 'hover:border-[#075FA3] hover:text-[#075FA3]'
                      }`}
                      data-testid={`time-slot-${time.replace(/\s|:/g, '-')}`}
                    >
                      {time}
                    </Button>
                  );
                })}
              </div>
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setBookingStep(1)}
                  className="flex-1 rounded-full py-6"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setBookingStep(3)}
                  disabled={!selectedTime}
                  className="flex-1 bg-[#075FA3] hover:bg-[#0077B6] text-white rounded-full py-6"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {bookingStep === 3 && (
            <form onSubmit={handleSubmitBooking} className="space-y-4">
              <div className="bg-[#F3F4F6] rounded-xl p-4 mb-4">
                <p className="text-sm text-[#58595B] mb-2">
                  <span className="font-semibold text-[#231F20]">
                    {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                  </span>
                  {' '}at{' '}
                  <span className="font-semibold text-[#231F20]">{selectedTime}</span>
                </p>
                <p className="text-sm text-[#58595B]">
                  Booking as: <span className="font-semibold text-[#231F20]">{startupInfo.startup_name}</span>
                </p>
              </div>

              <div>
                <Label htmlFor="topic" className="form-label">Topic of Discussion *</Label>
                <Textarea
                  id="topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  required
                  placeholder="What would you like to discuss with the advisor?"
                  className="form-input min-h-[100px]"
                  data-testid="booking-topic-input"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setBookingStep(2)}
                  className="flex-1 rounded-full py-6"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !topic}
                  className="flex-1 bg-[#C83041] hover:bg-[#A92332] text-white rounded-full py-6"
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? 'Booking...' : 'Confirm Booking'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
